/**
 * Eén lijst van alle rechten van een account (05-10-2026, Jordy: "één gelijk
 * rechtenscherm"), gebruikt door Settings → Users (aanmaken en bewerken) en
 * Chatters (aanmaken en Edit chatter). Alles staat op één plek:
 * `admin_users.allowed_pages` van de login. Het blok werkt op die tokenlijst;
 * de Chatters-dialogen vertalen hem naar de vlaggen van hun route
 * (`vlaggenUitTokens`), zodat beide ingangen precies hetzelfde opslaan.
 *
 * Dubbel waren (zelfde token, twee vinkjes): "Chatters" (Users) = "Allow team
 * performance dashboard" (Edit chatter) → token `chatters`; "Mass DM via
 * Chats" (Users-schuif) = "Mass DM via Chats" (Edit chatter) → `mass_dm_native`.
 * Hier staat elk token één keer.
 *
 * Geen React: de tests voeren dit uit met node.
 */
export type RechtSoort = 'token' | 'weiger' | 'vip'

export interface Recht {
  /** Sleutel in het scherm en, voor Chatters, de vlag van de route. */
  sleutel: string
  label: string
  /** Het token in allowed_pages. Bij `weiger` betekent het token "uit". */
  token: string
  soort: RechtSoort
  groep: 'paginas' | 'rechten' | 'filters' | 'massa'
  rollen: string[]
  /** Alleen voor een head chatter (lead). */
  alleenLead?: boolean
  /** De vlag van de Chatters-routes (allow_*); leeg = route kent hem als page_tokens. */
  vlag?: string
  /** Standaardwaarde bij een nieuw account zonder token. */
  standaard?: boolean
  hint?: string
  /** 07-10-2026 (Jordy): ook een admin mag dit recht zetten (anders alleen owner). */
  ookAdmin?: boolean
}

const CHATTER = ['chatter', 'viewer']

export const RECHTEN: Recht[] = [
  // Pagina's van een chatter (manager/owner gebruiken hun rol, niet deze lijst).
  { sleutel: 'conversations', label: 'Chats', token: 'conversations', soort: 'token', groep: 'paginas', rollen: CHATTER, standaard: true },
  { sleutel: 'personas', label: 'Models', token: 'personas', soort: 'token', groep: 'paginas', rollen: CHATTER, standaard: true },
  { sleutel: 'fans', label: 'Fans', token: 'fans', soort: 'token', groep: 'paginas', rollen: CHATTER, standaard: true },
  { sleutel: 'earnings', label: 'Earnings', token: 'earnings', soort: 'token', groep: 'paginas', rollen: CHATTER, standaard: true },
  { sleutel: 'allow_followers', label: 'Follower Tracking', token: 'followers', soort: 'token', groep: 'paginas', rollen: CHATTER, vlag: 'allow_followers' },
  { sleutel: 'allow_chatter_dashboard', label: 'Team performance (Chatters)', token: 'chatters', soort: 'token', groep: 'paginas', rollen: CHATTER, vlag: 'allow_chatter_dashboard' },
  // 07-10-2026 (Jordy): de Mass Messages-pagina staat onder Pages, voor een
  // chatter en een manager (niet voor een viewer), en een admin mag hem ook
  // zetten. Een chatter ziet daar alleen zijn toegewezen modellen (server).
  { sleutel: 'mass_messages_page', label: 'Mass Messages page', token: 'mass_messages_page', soort: 'token', groep: 'paginas', rollen: ['chatter', 'manager'], ookAdmin: true,
    hint: 'Overview, Create, Drafts and Agenda, both methods, and Mass DM from Chats. A chatter sees only their assigned models and real fans. Never “Test accounts only”.' },
  // 23-10-2026 (Jordy, Content fase 2): de Content-pagina en downloaden, voor
  // een chatter en een manager; standaard uit, alleen de owner zet ze. De
  // grens per download (N) en de 24-uursgrens staan bij de gebruiker.
  { sleutel: 'content_page', label: 'Content page', token: 'content_page', soort: 'token', groep: 'paginas', rollen: ['chatter', 'manager'],
    hint: 'View the media libraries. A chatter sees only their assigned models and only folders that are visible to chatters.' },
  { sleutel: 'content_download', label: 'Content download', token: 'content_download', soort: 'token', groep: 'paginas', rollen: ['chatter', 'manager'],
    hint: 'Download Processed items, at most the per-download maximum at once; every item counts toward the 24-hour limit.' },
  // 24-10-2026 (Jordy): Instagram fase 1, standaard uit. Een chatter ziet alleen de
  // accounts van zijn toegewezen modellen (server).
  { sleutel: 'instagram_page', label: 'Social page', token: 'instagram_page', soort: 'token', groep: 'paginas', rollen: ['manager'],
    hint: 'Social page (Instagram, Threads, X) for assigned models: posts, comments, replies. Dry run: nothing is sent.' },
  // 24-10-2026 (Jordy): goedkeuren van posts en sjabloonvoorstellen ("Approved by").
  { sleutel: 'instagram_approve', label: 'Social approve', token: 'instagram_approve', soort: 'token', groep: 'paginas', rollen: ['manager'],
    hint: 'May press Approve on posts and template replies after checking them. Needs the Social page too.' },
  // Mass Messages-schuiven.
  { sleutel: 'allow_native_mass_dm', label: 'Mass DM via Chats', token: 'mass_dm_native', soort: 'token', groep: 'massa', rollen: CHATTER, vlag: 'allow_native_mass_dm',
    hint: 'One Mass DM at a time from Chats, Platform Mass DM only, real fans.' },
  { sleutel: 'allow_paid_mass_dm', label: 'Paid Mass DM (PPV pricing)', token: 'mass_dm_no_paid', soort: 'weiger', groep: 'massa', rollen: CHATTER, vlag: 'allow_paid_mass_dm' },
  // Overige rechten van een chatter.
  { sleutel: 'allow_private_self_claim', label: 'Private to me (self claim)', token: 'private_self_claim', soort: 'token', groep: 'rechten', rollen: CHATTER, vlag: 'allow_private_self_claim' },
  { sleutel: 'allow_private_manage_all', label: 'Manage all private chats', token: 'private_manage_all', soort: 'token', groep: 'rechten', rollen: CHATTER, vlag: 'allow_private_manage_all' },
  { sleutel: 'allow_timewaster_mark', label: 'Mark as Timewaster', token: 'deny_timewaster_mark', soort: 'weiger', groep: 'rechten', rollen: CHATTER, vlag: 'allow_timewaster_mark', standaard: true },
  { sleutel: 'allow_vip_mark', label: 'Mark as VIP', token: 'vip_mark', soort: 'vip', groep: 'rechten', rollen: CHATTER, vlag: 'allow_vip_mark', standaard: true },
  { sleutel: 'allow_toggle_ai', label: 'AI on/off (Resume AI)', token: 'toggle_ai', soort: 'token', groep: 'rechten', rollen: CHATTER, vlag: 'allow_toggle_ai' },
  { sleutel: 'hide_earnings', label: 'Hide earnings', token: 'hide_earnings', soort: 'token', groep: 'rechten', rollen: CHATTER, vlag: 'hide_earnings' },
  { sleutel: 'view_all_unlocks', label: 'View all Unlocks & Tips', token: 'view_all_unlocks', soort: 'token', groep: 'rechten', rollen: CHATTER, vlag: 'view_all_unlocks' },
  { sleutel: 'allow_create_chatter_accounts', label: 'Create regular chatter accounts', token: 'create_chatter_accounts', soort: 'token', groep: 'rechten', rollen: ['chatter'], vlag: 'allow_create_chatter_accounts', alleenLead: true },
  // Mass DM-filters (wat een chatter zelf mag zetten).
  ...([
    ['private_chats', 'Private chats (special-need / claimed)'],
    ['timewasters', 'Timewasters'],
    ['spenders', 'Paying fans'],
    ['new_followers', 'New followers'],
    ['recent_replies', 'Exclude recent replies'],
    ['online_only', 'Online fans only'],
    ['audience', 'Send-to audience groups'],
  ] as const).map(([k, label]): Recht => ({
    sleutel: `filter_${k}`, label, token: `mass_dm_filter_${k}`, soort: 'token', groep: 'filters', rollen: CHATTER,
  })),
]

/** De rechten die voor deze rol betekenis hebben. */
export function rechtenVoorRol(rol: string, isLead = false): Recht[] {
  const r = String(rol || '').toLowerCase()
  return RECHTEN.filter((x) => x.rollen.includes(r) && (!x.alleenLead || isLead))
}

/** Staat dit recht aan in deze tokenlijst? */
export function staatAan(pages: string[], recht: Recht): boolean {
  if (recht.soort === 'weiger') return !pages.includes(recht.token)
  if (recht.soort === 'vip') {
    if (pages.includes('deny_vip_mark')) return false
    if (pages.includes('vip_mark')) return true
    return !pages.includes('deny_timewaster_mark') // zoals de server: volgt Timewaster
  }
  return pages.includes(recht.token)
}

/** Zet dit recht aan of uit; laat alle andere tokens staan. */
export function zetRecht(pages: string[], recht: Recht, aan: boolean): string[] {
  if (recht.soort === 'vip') {
    const rest = pages.filter((p) => p !== 'vip_mark' && p !== 'deny_vip_mark')
    return [...rest, aan ? 'vip_mark' : 'deny_vip_mark']
  }
  const rest = pages.filter((p) => p !== recht.token)
  const tokenAan = recht.soort === 'weiger' ? !aan : aan
  return tokenAan ? [...rest, recht.token] : rest
}

/** Tokens voor een nieuw account van deze rol (de standaardwaarden). */
export function standaardTokens(rol: string): string[] {
  let pages: string[] = []
  for (const r of rechtenVoorRol(rol)) {
    // VIP zonder keuze volgt Timewaster (aan); geen expliciet token nodig.
    if (r.soort === 'vip') continue
    if (r.soort === 'weiger') { if (r.standaard === false) pages = zetRecht(pages, r, false) }
    else if (r.standaard) pages = zetRecht(pages, r, true)
  }
  // Betaalde Mass DM staat bij een nieuw chatteraccount uit (server: trial-vloer).
  if (rechtenVoorRol(rol).some((r) => r.sleutel === 'allow_paid_mass_dm')) {
    pages = zetRecht(pages, RECHTEN.find((r) => r.sleutel === 'allow_paid_mass_dm') as Recht, false)
  }
  return pages
}

/** Voor de Chatters-routes: vlaggen, filters en paginarechten uit de tokenlijst. */
export function vlaggenUitTokens(pages: string[], isLead = false): Record<string, unknown> {
  const uit: Record<string, unknown> = {}
  for (const r of rechtenVoorRol('chatter', isLead)) if (r.vlag) uit[r.vlag] = staatAan(pages, r)
  uit.mass_dm_filter_grants = RECHTEN.filter((r) => r.groep === 'filters' && pages.includes(r.token)).map((r) => r.token)
  uit.page_tokens = RECHTEN.filter((r) => r.groep === 'paginas' && !r.vlag && pages.includes(r.token)).map((r) => r.token)
  return uit
}

/** Omgekeerd: de tokenlijst uit wat de Chatters-route teruggeeft. */
export function tokensUitChatter(c: Record<string, unknown>): string[] {
  let pages: string[] = Array.isArray(c.page_tokens) ? [...(c.page_tokens as string[])] : []
  for (const r of rechtenVoorRol('chatter', true)) {
    if (r.vlag && r.vlag in c) pages = zetRecht(pages, r, Boolean(c[r.vlag]))
  }
  const filters = Array.isArray(c.mass_dm_filter_grants) ? (c.mass_dm_filter_grants as string[]) : []
  for (const f of filters) {
    const token = f.startsWith('mass_dm_filter_') ? f : `mass_dm_filter_${f}`
    if (!pages.includes(token)) pages.push(token)
  }
  return pages
}
